<script setup>
import {
  checkEmailDuplicationAPI,
  checkPasswordAPI,
  getUserInfoAPI,
  mypageResetEmailAPI,
  mypageResetInfoAPI,
  mypageResetMobileAPI,
  mypageResetPasswordAPI,
  updateBasicInfoAPI,
  mypageExistPasswordAPI,
  mypageCheckPasswordNoticeAPI,
} from '@/api/myPage';
import AuthApi from '@/api/Auth';
import { useRnMessage } from '@/composables/useRnMessage';
import { useRnBridge } from '@/composables/useRnBridge';
import { useAppBack, useBackLayer, useBackReducer } from '@/composables/useAppBack';
import { MyinfoContent, AuthTab, EditTab, DeleteTab, EditModal } from '@/components/mypage';
import { formatDateTime, formatMobileNo } from '@/composables/dataFormatter';
import { DEV_MODE } from '@/config/dev-mode';
import { checkAuthNoAPI, checkMobileDuplicationAPI, sendAuthAPI, sendMeAuthAPI } from '@/api/Join';
import {
  emailValidationCheck,
  validateMobile,
  validateUserId,
} from '@/composables/validationCheck';
import { watch, onMounted } from 'vue';
import { PopupModal } from '@/components/auth';
import { useMyInfoStore } from '@/store/modules/myinfo';
import Snackbar from '@/components/common/Snackbar.vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import ConfirmComponent from '@/components/common/Confirm.vue';
import SnsPwLayer from '@/components/mypage/SnsPwLayer.vue';

import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'mypage' } });

const creditInfoRef = ref(null);
const myInfoStore = useMyInfoStore();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { performOneBack } = useAppBack();
const { isSmartwelMember } = useMember();
/**
 * 내 정보 페이지 탭
 *
 * 'view' | 'auth' | 'edit' | 'delete'
 */
const myInfoTab = ref('view');
/** 인증 성공 후 이동할 목적지 탭 */
const targetTabAfterAuth = ref('edit');

/** 내 정보 > 'view' 정보 */
const accountInfo = reactive({
  userId: '',
  password: '',
  name: '',
  nickname: '',
  email: '',
  mobileNo: '',
  createdAt: '',
  loggedAt: '',
  isCheckPasswordNotice: false,
  oauthAccount: null,
  oauthAccounts: [], // 연동된 소셜 계정 목록 (provider별 [연동] 버튼 비활성화 판단용)
  hasSignAccount: false, // ID/PW 로그인 수단 보유 여부 (소셜 전체 해제 허용 판단용)
});

/**
 * 인증 수단 탭
 *
 * 0: 비밀번호 인증, 1: 휴대폰 문자 인증
 */
const authMethodTab = ref(0);

/** AuthTab - 인증 단계 사용자 입력값 */
const userAuthInfo = reactive({
  userPwd: '',
  mobileNo: '',
  authNo: '',
  sendKey: '',
});

/** AuthTab - 인증 단계 Status 관리 */
const authStatus = ref({
  /** 인증번호 발송 상태 */
  isSentAuth: false,
  /** 인증번호 입력창 활성화 여부 */
  showAuthNo: false,
  /** 타이머 동작 상태  */
  timerState: 'stop',
});
/** AuthTab - 인증번호 발송 버튼 텍스트 */
const sendButtonText = computed(() => {
  // 인증번호 발송전 또는 발송후 시간 만료된 경우
  if (!authStatus.value.isSentAuth) return '인증번호 발송';
  // 인증번호 발송후, 발송후 입력값 변경
  if (authStatus.value.isSentAuth) return '재발송';
});
/** AuthTab - 제출 버튼 활성화 여부 */
const isConfirmDisabled = computed(() => {
  if (myInfoTab.value === 'auth') {
    if (
      authMethodTab.value === 0 &&
      userPwdValidation.isValid &&
      userAuthInfo.userPwd &&
      userAuthInfo.userPwd.length > 7
    ) {
      return false;
    }
    if (
      authMethodTab.value === 1 &&
      authNoValidation.isValid &&
      userAuthInfo.authNo &&
      authStatus.value.isSentAuth
    ) {
      return false;
    }
  }
  return true;
});

const userEditData = reactive({
  loginId: '',
  /** 제출용 비밀번호 */
  loginPwd: '',
  mobileNo: '',
  authNo: '',
  name: '',
  sendKey: '',
  /** 새 비밀번호 */
  userPwd: '',
  /** 새 비밀번호 확인 */
  pwdCheck: '',
  email: '',
  nickname: '',
});

/** EditTab - 비밀번호 재설정 상태 */
const showEditPassword = ref(false);
/** EditTab - 닉네임 수정 상태 (모바일 모달용) */
const showEditNickname = ref(false);

/** EditTab - 이메일 재설정 정보 */
const editEmailInfo = reactive({
  sendKey: '',
  authNo: '',
  isSentAuth: false,
  showAuthNo: false,
  timerState: 'stop',
});
/** EditTab - 이메일 재설정 상태 */
const showEditEmail = ref(false);
/** EditTab - 선택된 이메일 도메인 항목  */
const selectedDomain = ref('직접 입력');
/** EditTab - '이메일 재설정' 버튼 텍스트 */
const editEmailButtonText = computed(() => {
  if (accountInfo.email !== null) return '이메일 재설정';
  if (showEditEmail.value) return '인증번호 발송';
  return '인증번호 발송';
});
/** EditTab - '이메일 재설정' 버튼 활성화 상태 */
const editEmailButtonActive = computed(() => {
  if (accountInfo.email === null) return userEditData.email !== '';
  if (showEditEmail.value) return userEditData.email !== '';
  return true;
});

/** EditTab - 휴대폰 번호 재설정 정보 */
const editMobileInfo = reactive({
  sendKey: '',
  authNo: '',
  isSentAuth: false,
  showAuthNo: false,
  timerState: 'stop',
});
/** EditTab - 휴대폰 번호 재설정 상태 */
const showEditMobileNo = ref(false);
/** EditTab - '휴대폰 번호 재설정' 버튼 텍스트 */
const editMobileButtonText = computed(() => {
  if (accountInfo.mobileNo.length === 0) return '휴대폰 번호 등록';
  return '휴대폰 번호 재설정';
});

// 팝업 상태 관리
const showPopup = ref(false);
const popupMessage = ref('');
const popupButtonText = ref('확인');

/** 스낵바 상태관리 */
const snackbar = ref(null);

/** SNS 비밀번호 레이어 표시 여부 */
const showSnsPwLayer = ref(false);

const handleOpenSnsPwLayer = () => {
  showSnsPwLayer.value = true;
};

const handleCloseSnsPwLayer = () => {
  showSnsPwLayer.value = false;
};

const handleSnsPwSuccess = async () => {
  showSnsPwLayer.value = false;
  accountInfo.isCheckPasswordNotice = true;
  await loadUserInfo();
};

// ========================= 모바일 앱과 통신 =========================
const insetBottom = ref(20);
/** 사용자 정보 로드 여부 */
const isUserInfoLoaded = ref(false);

/** 사용자 정보 로드 함수 */
const loadUserInfo = async () => {
  // 이미 로드된 경우 중복 호출 방지
  if (isUserInfoLoaded.value) {
    return;
  }

  try {
    await refreshUpdatedAt({});
    const isPasswordSet = await mypageExistPasswordAPI();
    if (isPasswordSet) {
      accountInfo.password = '********';
    } else {
      accountInfo.password = '미설정';
      // 개발 환경의 복지찬스 회원은 비밀번호 등록 안내 자동 오픈을 건너뜀(테스트 편의)
      const skipPwNotice = DEV_MODE && isSmartwelMember.value;
      if (!accountInfo.isCheckPasswordNotice && !skipPwNotice) {
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
          handleOpenSnsPwLayer();
        } else {
          router.push('/mypage/snsPw');
        }
        mypageCheckPasswordNoticeAPI();
      }
    }
    isUserInfoLoaded.value = true;
  } catch (e) {
    console.error('Failed to load user info:', e);
  }
};

/** 다이얼로그 Confirm 관련 */
const dialogConfrim = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);

/** 다이얼로그 Confirm 함수 */
const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm;
  dialogConfrim.value.openConfirmDialog();
};

/** EditTab - 소셜 계정 연동 해제 */
const PROVIDER_LABELS_KR = { kakao: '카카오', naver: '네이버', google: '구글', apple: '애플' };

const onUnlinkProvider = (provider) => {
  const label = PROVIDER_LABELS_KR[provider] || provider;
  showDialogConfirm(`${label} 계정 연동을 해제할까요?`, '', '연동 해제', async () => {
    try {
      const result = await AuthApi.unlinkOAuth(provider);
      if (result?.unlinked) {
        triggerSnackbar(`${label} 계정 연동이 해제되었습니다.`);
      } else {
        triggerSnackbar('해제 결과를 확인할 수 없습니다.');
      }
      isUserInfoLoaded.value = false;
      await loadUserInfo();
    } catch (e) {
      const status = e?.response?.status;
      DEV_MODE && console.warn('[unlink] 실패', { status, error: e?.response?.data });
      triggerSnackbar('연동 해제에 실패했습니다.');
    }
  });
};

/** 사용자 입력 - 닉네임 유효성 상태 */
const nicknameValidation = reactive({
  data: 'nickname',
  isValid: false,
  validationMessage: '',
});
/** 사용자 입력 - 비밀번호 유효성 상태 */
const userPwdValidation = reactive({
  data: 'userPwd',
  isValid: false,
  validationMessage: '',
});
/** 사용자 입력 - 비밀번호 확인 유효성 상태 */
const pwdCheckValidation = reactive({
  data: 'pwdCheck',
  isValid: false,
  validationMessage: '',
});
/** 사용자 입력 - 이메일 유효성 상태 */
const emailValidation = reactive({
  data: 'email',
  isValid: false,
  validationMessage: '',
});
/** 사용자 입력 - 휴대폰 번호 유효성 상태 */
const mobileValidation = reactive({
  data: 'mobileNo',
  isValid: false,
  validationMessage: '',
});
/** 사용자 입력 - 인증번호 유효성 상태 */
const authNoValidation = reactive({
  data: 'authNo',
  isValid: false,
  validationMessage: '',
});

onMounted(async () => {
  // 내 정보 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('MY_myinfo_view');
  await loadUserInfo();
  // 웹 브라우저(비-웹뷰) 환경: returnPath query로 콜백 처리
  await handleOAuthLinkCallbackFromQuery();
});

// 웹뷰 환경: 앱이 시스템 시트 close 후 oauthLinkResult 메시지 전송
useRnMessage({
  onCustom: ({ action, payload }) => {
    if (action !== 'oauthLinkResult') return;
    processOAuthLinkResult({
      code: payload?.code,
      error: payload?.error,
    });
  },
});

/**
 * OAuth 추가 연동 콜백 처리 (웹 브라우저 흐름).
 * returnPath의 ?code/?error query를 트리거로 사용.
 */
const handleOAuthLinkCallbackFromQuery = async () => {
  const code = route.query.code;
  const error = route.query.error;
  if (!code && !error) return;

  // URL의 code/error query는 정리 (새로고침 시 재처리 방지)
  router.replace({ path: route.path, query: {} });
  await processOAuthLinkResult({ code, error });
};

/**
 * 공통 후속 처리: sessionStorage의 verifier로 POST /oauth/link/{provider}.
 * 입력 source는 두 가지 — query string (웹) / RN message (앱).
 */
const { debuggingHandler } = useRnBridge();
const linkDebug = (message) => {
  const tagged = `[link] ${message}`;
  // chrome://inspect / Safari Develop의 web devtools 에서 보기 위한 console
  console.log(tagged);
  // RN 콘솔(Metro)에도 함께 전송
  debuggingHandler({ action: 'debug', message: tagged });
};

const processOAuthLinkResult = async ({ code, error }) => {
  linkDebug(
    `1. processOAuthLinkResult 진입. code=${code ? String(code).slice(0, 12) : 'none'} error=${error || 'none'}`,
  );
  const stateRaw = sessionStorage.getItem('oauth_link_state');
  let provider = null;
  let codeVerifier = null;
  if (stateRaw) {
    try {
      const parsed = JSON.parse(stateRaw);
      provider = parsed.provider;
      codeVerifier = parsed.codeVerifier;
    } catch {}
    sessionStorage.removeItem('oauth_link_state');
  }
  linkDebug(
    `2. stateRaw=${stateRaw ? 'present' : 'missing'} provider=${provider} verifier=${codeVerifier ? 'present' : 'missing'}`,
  );

  if (error) {
    linkDebug(`3. error 분기 (early return): ${error}`);
    if (error !== 'user_cancelled') {
      triggerSnackbar('연동이 취소되었거나 실패했습니다.');
    }
    return;
  }
  if (!provider || !codeVerifier || !code) {
    linkDebug(
      `3. 필수값 누락 (early return). provider=${!!provider} verifier=${!!codeVerifier} code=${!!code}`,
    );
    triggerSnackbar('연동 세션이 만료되었습니다. 다시 시도해주세요.');
    return;
  }

  try {
    linkDebug(`4. POST /oauth/link/${provider} 호출`);
    const result = await AuthApi.completeOAuthLink(provider, code, codeVerifier);
    linkDebug(`5. 응답: ${JSON.stringify(result)}`);
    if (result.alreadyLinked) {
      triggerSnackbar('이미 연동된 계정입니다.');
    } else {
      triggerSnackbar('소셜 계정 연동이 완료되었습니다.');
    }
    isUserInfoLoaded.value = false;
    await loadUserInfo();
  } catch (e) {
    const status = e?.response?.status;
    const errCode = e?.response?.data?.error;
    linkDebug(
      `ERROR: status=${status} errCode=${errCode} data=${JSON.stringify(e?.response?.data)}`,
    );
    if (status === 409) {
      triggerSnackbar('이미 다른 계정에 연동된 소셜 로그인입니다.');
    } else if (errCode === 'invalid_grant') {
      triggerSnackbar('인증 세션이 만료되었습니다. 다시 시도해주세요.');
    } else {
      triggerSnackbar('연동에 실패했습니다.');
    }
  }
};

/** AuthTab - 인증번호 발송 상태 변경 */
watch(
  () => authStatus.value.isSentAuth,
  (newState) => {
    if (!newState) {
      if (authStatus.value.timerState === 'stop') {
        authNoValidation.isValid = false;
        authNoValidation.validationMessage =
          '인증번호 입력시간이 초과되었습니다. 인증번호를 다시 발송해 주세요.';
      } else if (userAuthInfo.sendKey) {
        userAuthInfo.sendKey = null;
        userAuthInfo.authNo = '';
        authNoValidation.isValid = false;
        authNoValidation.validationMessage = '변경된 정보가 있습니다. 인증을 다시 진행해 주세요.';
      }
    }
    if (newState) authNoValidation.validationMessage = '';
  },
);
watch(
  () => editEmailInfo.isSentAuth,
  (newState) => {
    if (!newState) {
      if (editEmailInfo.timerState === 'stop') {
        emailValidation.isValid = false;
        if (editEmailInfo.authNo === '') emailValidation.validationMessage = '';
        else
          emailValidation.validationMessage =
            '인증번호 입력시간이 초과되었습니다. 인증번호를 다시 발송해 주세요.';
      } else if (editEmailInfo.sendKey) {
        editEmailInfo.sendKey = null;
        editEmailInfo.authNo = '';
        emailValidation.isValid = false;
        emailValidation.validationMessage = '변경된 정보가 있습니다. 인증을 다시 진행해 주세요.';
      }
    } else emailValidation.validationMessage = '';
  },
);
watch(
  () => editMobileInfo.isSentAuth,
  (newState) => {
    if (!newState) {
      if (editMobileInfo.timerState === 'stop') {
        mobileValidation.isValid = false;
        if (editMobileInfo.authNo === '') mobileValidation.validationMessage = '';
        else
          mobileValidation.validationMessage =
            '인증번호 입력시간이 초과되었습니다. 인증번호를 다시 발송해 주세요.';
      } else if (editMobileInfo.sendKey) {
        editMobileInfo.sendKey = null;
        editMobileInfo.authNo = '';
        mobileValidation.isValid = false;
        mobileValidation.validationMessage = '변경된 정보가 있습니다. 인증을 다시 진행해 주세요.';
      }
    } else mobileValidation.validationMessage = '';
  },
);

/** AuthTab - 인증수단 변경 */
watch(
  () => authMethodTab.value,
  (newTab, oldTab) => {
    if (oldTab === 1 && newTab === 0) {
      authStatus.value.isSentAuth = false;
      authStatus.value.showAuthNo = false;
      authStatus.value.timerState = 'stop';
      userAuthInfo.authNo = '';
      userAuthInfo.sendKey = null;
    }
  },
);

/** 스낵바 표시 */
const triggerSnackbar = (message) => {
  snackbar.value.showSnackbar(message);
};

/** 이름 입력 핸들러 */
const inputNameHandler = (value) => {
  authStatus.value.isSentAuth = false;

  if (value === '') {
    nameValidation.isValid = false;
    nameValidation.validationMessage = '';
  } else if (!validateName(value)) {
    nameValidation.isValid = false;
    nameValidation.validationMessage = '이름은 한글, 영문만 사용 가능합니다.';
  } else {
    nameValidation.isValid = true;
    nameValidation.validationMessage = '';
  }
};
/** 닉네임 입력 핸들러 */
const inputNicknameHandler = (value) => {
  if (value && value.length > 0 && value.length < 21) nicknameValidation.isValid = true;
  else nicknameValidation.isValid = false;
};
/** 휴대폰 번호 입력 핸들러 */
const inputMobileHandler = (value) => {
  authStatus.value.isSentAuth = false;
  editMobileInfo.isSentAuth = false;

  if (value === '') {
    mobileValidation.isValid = false;
    mobileValidation.validationMessage = '';
  } else if (!validateMobile(value)) {
    mobileValidation.isValid = false;
    mobileValidation.validationMessage = '올바르지 않은 전화번호입니다.';
  } else {
    mobileValidation.isValid = true;
    mobileValidation.validationMessage = '';
  }
};
/** 인증번호 입력 핸들러 */
const inputAuthHandler = (value) => {
  if (value && value.length > 0) authNoValidation.isValid = true;
  else authNoValidation.isValid = false;
};
/** 아이디 입력 핸들러 */
const inputUserIdHandler = (value) => {
  if (value === '') {
    userIdValidation.isValid = false;
    userIdValidation.validationMessage = '';
  } else if (!validateUserId(value)) {
    userIdValidation.isValid = false;
    userIdValidation.validationMessage =
      '아이디는 5~20자 영문 소문자, 숫자 조합으로 입력해 주세요.';
  } else {
    userIdValidation.isValid = true;
    userIdValidation.validationMessage = '';
  }
};
/** 비밀번호 입력 핸들러 */
const inputPasswordHandler = (value) => {
  if (value === '') {
    userPwdValidation.isValid = false;
    userPwdValidation.validationMessage = '';
  } else if (!validatePassword(value)) {
    userPwdValidation.isValid = false;
    userPwdValidation.validationMessage =
      '비밀번호는 8~15자 영문, 숫자, 특수문자 조합으로 입력해 주세요.';
  } else {
    userPwdValidation.isValid = true;
    userPwdValidation.validationMessage = '';
  }

  if (myInfoTab.value === 'edit') {
    inputPassCheckHandler({
      userPwd: value,
      pwdCheck: userEditData.pwdCheck,
    });
  }
};
/** 비밀번호 확인 핸들러 */
const inputPassCheckHandler = ({ userPwd, pwdCheck }) => {
  const targetPwd = userPwd || userEditData.userPwd;
  if (pwdCheck === '') {
    pwdCheckValidation.isValid = false;
    pwdCheckValidation.validationMessage = '';
    userEditData.loginPwd = '';
  } else if (pwdCheck !== targetPwd) {
    pwdCheckValidation.isValid = false;
    pwdCheckValidation.validationMessage =
      '비밀번호가 일치하지 않습니다. 입력한 비밀번호를 확인해 주세요.';
    userEditData.loginPwd = '';
  } else {
    pwdCheckValidation.isValid = true;
    pwdCheckValidation.validationMessage = '';
    userEditData.loginPwd = targetPwd;
  }
};
const inputEmailHandler = (value) => {
  editEmailInfo.isSentAuth = false;

  if (value === '') {
    emailValidation.isValid = false;
    emailValidation.validationMessage = '';
  } else if (!emailValidationCheck(value)) {
    emailValidation.isValid = false;
    emailValidation.validationMessage = '올바른 이메일 형식이 아닙니다.';
  } else {
    emailValidation.isValid = true;
    emailValidation.validationMessage = '';
  }
};
/** 통합 입력값 핸들러 */
const inputHandler = ({ value, dataType }) => {
  value = value.trim();
  switch (dataType) {
    case 'name':
      inputNameHandler(value);
      break;
    case 'nickname':
      inputNicknameHandler(value);
      break;
    case 'email':
      inputEmailHandler(value);
      break;
    case 'mobileNo':
      value = value.replace(/\D/g, '');
      inputMobileHandler(value);
      break;
    case 'authNo':
      value = value.replace(/\D/g, '');
      inputAuthHandler(value);
      break;
    case 'userId':
      inputUserIdHandler(value);
      break;
    case 'userPwd':
      inputPasswordHandler(value);
      break;
    case 'pwdCheck':
      inputPassCheckHandler({ pwdCheck: value });
      break;
    default:
      break;
  }
  return value;
};
/** 내 정보 요청 */
const refreshUpdatedAt = async ({}) => {
  try {
    const result = await getUserInfoAPI();

    if (result) {
      if (result.signAccount) {
        accountInfo.userId = result.signAccount.loginId;
        accountInfo.oauthAccount = null;
      } else {
        const provider = result.oauthAccount.tokenProvider;
        accountInfo.userId = PROVIDER_LABELS_KR[provider] || provider;
        accountInfo.oauthAccount = result.oauthAccount;
      }
      accountInfo.hasSignAccount = !!result.signAccount;
      accountInfo.oauthAccounts = Array.isArray(result.oauthAccounts) ? result.oauthAccounts : [];
      accountInfo.name = result.name;
      accountInfo.nickname = result.nickname;
      accountInfo.email = result.email;
      accountInfo.mobileNo = formatMobileNo(result.mobileNo);
      accountInfo.createdAt = formatDateTime(result.createdAt);
      accountInfo.loggedAt = formatDateTime(result.loggedAt);
      accountInfo.isCheckPasswordNotice = result.isCheckPasswordNotice;
    }
  } catch (e) {
    DEV_MODE && console.log('Error refreshing updatedAt:', e);
  } finally {
    await myInfoStore.fetchMyInfo();
  }
};
/** 내 정보 내에서 탭 변경 //TODO: 정보수정 버튼 */
const changeMyInfoTab = async (newTab, targetTab = null) => {
  // 01> sns 계정 비밀번호 등록 여부 확인
  const pwExist = await mypageExistPasswordAPI();
  if (!pwExist) {
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
      handleOpenSnsPwLayer();
    } else {
      await router.push('/mypage/snsPw');
    }
    return;
  }
  // 목적지 탭이 지정된 경우 저장
  if (targetTab) {
    targetTabAfterAuth.value = targetTab;
  }
  if (newTab === 'edit') {
    userEditData.nickname = accountInfo.nickname;
  }
  myInfoTab.value = newTab;
};

/** AuthTab - 인증번호 발송 버튼 클릭 */
const clickSendButton = async () => {
  userAuthInfo.authNo = '';
  authStatus.value.isSentAuth = false;

  try {
    const params = {
      type: 'mobile',
    };
    const result = await sendMeAuthAPI({ params });

    userAuthInfo.sendKey = result;

    authStatus.value.isSentAuth = true;
    authStatus.value.showAuthNo = true;
    if (myInfoTab.value === 'auth') {
      popupMessage.value =
        '인증번호를 발송했습니다.<br>인증번호가 오지 않으면 정보를<br>다시 확인해 주세요.';
      showPopup.value = true;
    }
    authStatus.value.timerState = 'reset';
    setTimeout(() => {
      authStatus.value.timerState = 'start';
    }, 50);
  } catch (e) {
    DEV_MODE && console.log('useJoin clickSendButton error:', e);
  }
};
/** AuthTab - 인증번호 타이머 시간 만료시 */
const timerEnded = () => {
  authStatus.value.timerState = 'stop';
  authStatus.value.isSentAuth = false;
  userAuthInfo.sendKey = null;
};

/** AuthTab - 인증번호 확인 요청 */
const checkAuthNo = async ({ authNo, sendKey }) => {
  try {
    const result = await checkAuthNoAPI(authNo, sendKey);
    return result;
  } catch (e) {
    DEV_MODE && console.log('useJoin checkAuthNo error:', e);
  }
};
/** AuthTab - 확인 버튼 클릭 */
const clickConfirm = async () => {
  if (myInfoTab.value === 'auth') {
    if (authMethodTab.value === 0) {
      await checkPassword();
      // 비밀번호 체크 API 가 없음.
    } else if (authMethodTab.value === 1) {
      const response = await checkAuthNo({
        authNo: userAuthInfo.authNo,
        sendKey: userAuthInfo.sendKey,
      });
      if (response) {
        triggerSnackbar('인증되었습니다.');
        changeMyInfoTab(targetTabAfterAuth.value);
      } else {
        popupMessage.value = '인증번호를 확인해 주세요.';
        showPopup.value = true;
      }
    }
  }
};

/** EditTab - 비밀번호 재설정 버튼 클릭 */
const clickEditPassword = async () => {
  showEditPassword.value = !showEditPassword.value;
};
/** EditTab - 인증번호 입력창 타이머 종료시 */
const modalTimerEnded = ({ type }) => {
  if (type === 'mail') {
    editEmailInfo.sendKey = null;
    editEmailInfo.isSentAuth = false;
    editEmailInfo.timerState = 'stop';
  } else if (type === 'mobile') {
  }
};
const isVerifiedEmail = ref(false);
/** EditTab - 이메일 인증 확인 */
const clickEditAuthConfirm = async ({ type }) => {
  if (showEditMobileNo.value) {
    if (type === 'mail') {
      const authStatus = await checkAuthNo({
        authNo: editEmailInfo.authNo,
        sendKey: editEmailInfo.sendKey,
      });
      if (!authStatus) {
        popupMessage.value = '인증번호를 다시 확인해주세요.';
        showPopup.value = true;
        return;
      } else {
        popupMessage.value = '이메일 인증이 완료되었습니다.';
        showPopup.value = true;
        isVerifiedEmail.value = true;
        resetEditEmail();
      }
      return;
    } else if (type === 'mobile') {
      const isExist = await checkMobileDuplicationAPI(userEditData.mobileNo);

      if (isExist) {
        popupMessage.value = '이미 사용중인 휴대폰 번호입니다.';
        showPopup.value = true;
        // showEditMobileNo.value = false;
        return;
      }

      const params = {
        mobileNo: userEditData.mobileNo,
        sendKey: editMobileInfo.sendKey,
        authNo: editMobileInfo.authNo,
      };
      const result = await mypageResetMobileAPI({ params });
      if (result) {
        await refreshUpdatedAt({});
        popupMessage.value = '휴대폰 인증이 완료되었습니다.';
        showPopup.value = true;
        showEditMobileNo.value = false;
      }
      return;
    }
  }

  try {
    if (type === 'mail') {
      const authStatus = await checkAuthNo({
        authNo: editEmailInfo.authNo,
        sendKey: editEmailInfo.sendKey,
      });

      if (!authStatus) {
        popupMessage.value = '인증번호를 다시 확인해주세요.';
        showPopup.value = true;
        return;
      }

      if (authStatus) {
        const useStatus = await checkEmailDuplication();
        if (useStatus === 'AVAILABLE') {
          const params = {
            email: userEditData.email,
            authNo: editEmailInfo.authNo,
            sendKey: editEmailInfo.sendKey,
          };
          const result = await mypageResetEmailAPI({ params });
          if (result) await refreshUpdatedAt({});
          resetEditEmail();
          showEditEmail.value = false;
          // userEditData.email = accountInfo.email;
        } else if (useStatus === 'USED') {
          popupMessage.value = '이미 사용중인 이메일 주소입니다.';
          showPopup.value = true;
        } else {
          popupMessage.value = '확인에 실패하였습니다.';
          showPopup.value = true;
        }
      }
    }
  } catch (e) {
    DEV_MODE && console.log('myinfo clickEditAuthConfirm error:', e);
  }
};

/** AuthTab - 비밀번호 인증 API 호출 */
const checkPassword = async () => {
  const data = {
    password: userAuthInfo.userPwd,
  };
  try {
    const result = await checkPasswordAPI({ data });
    if (result) {
      triggerSnackbar('인증되었습니다.');
      changeMyInfoTab(targetTabAfterAuth.value);
    } else {
      popupMessage.value = '비밀번호를 확인해 주세요.';
      showPopup.value = true;
    }
  } catch (e) {
    DEV_MODE && console.log('myinfo checkPassword error:', e);
  }
};

/** 비밀번호 재설정 정보 초기화 */
const resetEditPassword = () => {
  userEditData.userPwd = '';
  userEditData.pwdCheck = '';
  userEditData.loginPwd = '';
  userPwdValidation.isValid = false;
  userPwdValidation.validationMessage = '';
  pwdCheckValidation.isValid = false;
  pwdCheckValidation.validationMessage = '';
};

/** 이메일 재설정 정보 초기화 */
const resetEditEmail = () => {
  editEmailInfo.timerState = 'stop';
  editEmailInfo.isSentAuth = false;
  editEmailInfo.showAuthNo = false;
  editEmailInfo.sendKey = null;
  editEmailInfo.authNo = '';
  userEditData.email = '';
  emailValidation.isValid = false;
  emailValidation.validationMessage = '';
};

/** 휴대폰 번호 재설정 정보 초기화 */
const resetEditMobile = () => {
  editMobileInfo.timerState = 'stop';
  editMobileInfo.isSentAuth = false;
  editMobileInfo.showAuthNo = false;
  editMobileInfo.sendKey = null;
  editMobileInfo.authNo = '';
  userEditData.mobileNo = '';
};

/** EditTab - 이메일 중복 검사 */
const checkEmailDuplication = async () => {
  try {
    let status = 'AVAILABLE';
    const result = await checkEmailDuplicationAPI(userEditData.email);
    if (result) status = 'USED';
    return status;
  } catch (e) {
    DEV_MODE && console.log('useJoin checkMobileDuplication error:', e);
    return 'ERROR';
  }
};

const sendAuthMobile = async () => {
  editMobileInfo.isSentAuth = true;
  editMobileInfo.showAuthNo = true;
  try {
    const result = await sendAuthAPI({
      // name: accountInfo.name,
      mobileNo: userEditData.mobileNo,
      type: 'mobile',
    });
    editMobileInfo.sendKey = result.key;
    popupMessage.value =
      '인증번호를 발송했습니다.<br>인증번호가 오지 않으면 정보를<br>다시 확인해 주세요.';
    showPopup.value = true;
    editMobileInfo.timerState = 'reset';
    setTimeout(() => {
      editMobileInfo.timerState = 'start';
    }, 50);
  } catch (e) {
    DEV_MODE && console.log('useJoin clickSendButton error:', e);
    editMobileInfo.isSentAuth = false;
    editMobileInfo.showAuthNo = false;
  }
};

const clickEditMobileButton = async (event) => {
  if (!accountInfo.email) {
    popupMessage.value =
      '휴대폰 번호를 변경하시는 경우, 이메일 인증이<br>필요합니다. 이메일 등록이 되어있지 않은 경우,<br>이메일을 먼저 등록해 주세요.';
    showPopup.value = true;
    return;
  } else {
    if (!showEditMobileNo.value) showEditMobileNo.value = !event;
    else {
      if (editEmailButtonActive) {
        // await clickEmailSendButton();
        // editEmailInfo.showAuthNo = true;
      }
    }
  }
};

/** EditTab - 비밀번호 재설정 API 호출 */
const editPassword = async () => {
  const params = {
    password: userEditData.loginPwd,
  };
  try {
    const result = await mypageResetInfoAPI({ type: 'password', params });

    if (result) {
      popupMessage.value = `비밀번호가 변경되었습니다.`;
      popupButtonText.value = `확인`;
      showPopup.value = true;
    }
  } catch (e) {
    DEV_MODE && console.log('myinfo editPassword error:', e);
  } finally {
    showEditPassword.value = false;
  }
};

/** EditTab - 휴대폰 번호 재설정 API 호출 */
const editMobileNo = async () => {
  const params = {};
  try {
    const result = await mypageResetInfoAPI({ type: 'mobile', params });
  } catch (e) {
    DEV_MODE && console.log('myinfo editMobileNo error:', e);
  }
};

/** EditTab - 닉네임 변경 API 호출 */
const editNickname = async (event) => {
  // 모바일에서는 닉네임 수정 모달만 열기
  const showOnly = event && typeof event === 'object' && event.showOnly;
  if (showOnly) {
    showEditNickname.value = true;
    return;
  }

  const data = {
    nickname: userEditData.nickname,
  };
  try {
    await updateBasicInfoAPI({ data });
    await refreshUpdatedAt({});
    showEditNickname.value = false;
    triggerSnackbar('닉네임이 변경되었습니다.');
  } catch (e) {
    DEV_MODE && console.log('myinfo editNickname error:', e);
  }
};

/** 팝업 닫기 */
const closePopup = () => (showPopup.value = false);

/** EditTab -  */
const clickEmailSendButton = async () => {
  try {
    let key = null;
    editEmailInfo.isSentAuth = true;
    editEmailInfo.showAuthNo = true;
    editEmailInfo.timerState = 'reset';
    if (!showEditMobileNo.value) {
      const result = await sendAuthAPI({
        email: userEditData.email,
        type: 'mail',
      });
      key = result.key;
    } else {
      key = await sendMeAuthAPI({
        params: {
          type: 'mail',
        },
      });
    }
    editEmailInfo.sendKey = key;
    setTimeout(() => {
      editEmailInfo.timerState = 'start';
    }, 50);
  } catch (e) {
    DEV_MODE && console.log('useJoin clickSendButton error:', e);
    editEmailInfo.isSentAuth = false;
    editEmailInfo.showAuthNo = false;
    editEmailInfo.timerState = 'stop';
  }
};

/** EditTab - 정보 수정 모달 확인 버튼 클릭 */
const clickUpdateMyInfo = async () => {
  if (showEditNickname.value) {
    await editNickname();
    return;
  }
  if (showEditPassword.value) {
    await editPassword();
  } else if (showEditEmail.value) {
    if (editEmailButtonActive) {
      if (!editEmailInfo.isSentAuth) {
        await clickEmailSendButton();
        editEmailInfo.showAuthNo = true;
      } else {
        await clickEditAuthConfirm();
        closeEditModal();
      }
    }
  }
};

/** EditTab - '이메일 재설정' 버튼 클릭 */
const clickEditEmailButton = async (event) => {
  // 모바일에서는 모달만 열기 (인증 발송은 모달 내에서 처리)
  const showOnly = event && typeof event === 'object' && event.showOnly;

  if (accountInfo.email === null) {
    if (!showEditEmail.value) showEditEmail.value = true;
    if (!showOnly && editEmailButtonActive) {
      await clickEmailSendButton();
      editEmailInfo.showAuthNo = true;
    }
  } else {
    // '이메일 재설정' 버튼 클릭
    if (!showEditEmail.value) showEditEmail.value = true;
    else if (!showOnly) {
      // '인증번호 발송' 버튼 클릭
      if (editEmailButtonActive) {
        await clickEmailSendButton();
        editEmailInfo.showAuthNo = true;
      }
    }
  }
};

/** 정보 수정 모달 닫기 */
const closeEditModal = () => {
  showEditPassword.value = false;
  showEditNickname.value = false;
  showEditEmail.value = false;
  showEditMobileNo.value = false;
  resetEditPassword();
  resetEditEmail();
  resetEditMobile();
  isVerifiedEmail.value = false;
};

/** 뒤로가기 버튼 핸들러 */
const handleBack = async () => {
  if (isWebView.value) {
    await performOneBack();
    return;
  }
  if (myInfoTab.value !== 'view') {
    myInfoTab.value = 'view';
    return;
  }
  router.back();
};

// --- 웹뷰 판별 ---
const isWebView = ref(false);
onMounted(() => {
  isWebView.value = !!window.ReactNativeWebView;
});

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);
const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};
const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
  },
);

const isLoggedIn = computed(() => {
  const uid = store.getters['user/id'];
  const uname = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(uid || uname || userInfo?.name);
  } catch {
    return !!(uid || uname);
  }
});
const displayName = computed(() =>
  isLoggedIn.value ? myInfoStore?.displayName || store.getters['user/name'] || '' : '',
);
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');
const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};
const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({ path: '/auth/login', query: { to: route.fullPath } });
  closeMypageMenu();
};
const isMenuActive = (menuLink) => {
  const p = route.path;
  if (menuLink.name === '낭만분석')
    return (
      p.startsWith('/saju-service') ||
      p.startsWith('/saju-result') ||
      p.startsWith('/saju-insight') ||
      p.startsWith('/analysis')
    );
  return p.startsWith(menuLink.basePath || menuLink.link);
};
const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') router.push(menu.link);
  closeMypageMenu();
};
const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (e) {
    console.error('Logout error:', e);
  }
};

useBackLayer({
  id: 'mypage-myinfo-edit-modal',
  isOpen: computed(
    () =>
      showEditPassword.value ||
      showEditNickname.value ||
      showEditEmail.value ||
      showEditMobileNo.value,
  ),
  closeFromBack: closeEditModal,
});
useBackLayer({
  id: 'mypage-myinfo-message',
  isOpen: showPopup,
  closeFromBack: closePopup,
});
useBackLayer({
  id: 'mypage-myinfo-sns-password',
  isOpen: showSnsPwLayer,
  closeFromBack: handleCloseSnsPwLayer,
});
useBackReducer({
  id: 'mypage-myinfo-tab',
  canUnwind: () => myInfoTab.value !== 'view',
  getDepth: () => (myInfoTab.value === 'view' ? 0 : 1),
  unwindOne: () => {
    myInfoTab.value = 'view';
  },
});
</script>

<template>
  <div class="card-container mypage-info-container">
    <div class="inquiry-detail-header">
      <!-- 앱 웹뷰: 뒤로가기 -->
      <button v-if="isWebView" class="inquiry-detail-header__back" @click="handleBack">
        <IconHeaderBack />
      </button>
      <div class="inquiry-detail-header__title">
        {{ myInfoTab === 'edit' ? '내 정보 수정' : '내 정보' }}
      </div>
      <div class="inquiry-detail-header__right">
        <button
          v-if="myInfoTab === 'view'"
          class="inquiry-detail-header__edit"
          @click="changeMyInfoTab('auth', 'edit')"
        >
          수정
        </button>
        <!-- 모바일 웹: 사람 아이콘 -->
        <button v-if="!isWebView" class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <!-- 마이페이지 드롭다운 (모바일 웹 전용) -->
    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box" @click="creditInfoRef?.closeDropdown()">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>
              홈
            </button>
            <template v-if="isLoggedIn">
              <div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                >
                <span class="txt2">행복한 하루 되세요</span>
              </div>
            </template>
            <template v-else>
              <div class="txt-box">
                <span class="txt1">게스트</span>
                <span class="txt2">로그인 후 이용해주세요</span>
              </div>
            </template>
            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />
            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</router-link
                >
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</a
                >
              </template>
            </div>
            <template v-if="isLoggedIn">
              <MypageLnbTabMenu />
              <button class="btn-xs line" @click="handleLogout">로그아웃</button>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>

    <MyinfoContent
      v-if="myInfoTab === 'view'"
      :account-info="accountInfo"
      @change="changeMyInfoTab"
    />

    <AuthTab
      v-if="myInfoTab === 'auth'"
      :account-info="accountInfo"
      :user-auth-info="userAuthInfo"
      :auth-method-tab="authMethodTab"
      :user-pwd-validation="userPwdValidation"
      :mobile-validation="mobileValidation"
      :auth-no-validation="authNoValidation"
      :auth-status="authStatus"
      :send-button-text="sendButtonText"
      :input-handler="inputHandler"
      :timer-ended="timerEnded"
      :is-confirm-disabled="isConfirmDisabled"
      @update:authMethodTab="authMethodTab = $event"
      @send="clickSendButton"
      @confirm="clickConfirm"
    />

    <EditTab
      v-if="myInfoTab === 'edit'"
      :account-info="accountInfo"
      :user-edit-data="userEditData"
      :show-edit-password="showEditPassword"
      :show-edit-email="showEditEmail"
      :edit-email-info="editEmailInfo"
      :selected-domain="selectedDomain"
      :edit-email-button-text="editEmailButtonText"
      :edit-email-button-active="editEmailButtonActive"
      :show-edit-mobile-no="showEditMobileNo"
      :edit-mobile-button-text="editMobileButtonText"
      :nickname-validation="nicknameValidation"
      :email-validation="emailValidation"
      :input-handler="inputHandler"
      :edit-timer-ended="modalTimerEnded"
      :click-edit-auth-confirm="clickEditAuthConfirm"
      @edit-password="clickEditPassword"
      @update:nickname="editNickname"
      @update:showEditEmail="clickEditEmailButton"
      @update:selectedDomain="selectedDomain = $event"
      @update:showEditMobileNo="clickEditMobileButton"
      @unlink="onUnlinkProvider"
    />

    <DeleteTab v-if="myInfoTab === 'delete'" @change="changeMyInfoTab" />
  </div>

  <EditModal
    :account-info="accountInfo"
    :show-edit-password="showEditPassword"
    :show-edit-nickname="showEditNickname"
    :show-edit-email="showEditEmail"
    :show-edit-mobile-no="showEditMobileNo"
    :user-edit-data="userEditData"
    :selected-domain="selectedDomain"
    :edit-email-info="editEmailInfo"
    :edit-email-button-text="editEmailButtonText"
    :edit-email-button-active="editEmailButtonActive"
    @send:email="clickEmailSendButton"
    @update:showEditEmail="clickEditEmailButton"
    @verify="clickEditAuthConfirm"
    @close="closeEditModal"
    @confirm="clickUpdateMyInfo"
    :user-pwd-validation="userPwdValidation"
    :pwd-check-validation="pwdCheckValidation"
    :nickname-validation="nicknameValidation"
    :email-validation="emailValidation"
    :mobile-validation="mobileValidation"
    :auth-no-validation="authNoValidation"
    :input-handler="inputHandler"
    :modal-timer-ended="modalTimerEnded"
    :is-verified-email="isVerifiedEmail"
    :edit-mobile-info="editMobileInfo"
    @send:mobile="sendAuthMobile"
  />

  <PopupModal
    v-if="showPopup"
    :message="popupMessage"
    :buttonText="popupButtonText"
    :isVisible="showPopup"
    @close="closePopup"
  />

  <Snackbar ref="snackbar" />

  <Teleport to="body">
    <transition name="layer">
      <div v-if="showSnsPwLayer" class="mypage-layer-overlay" @click.self="handleCloseSnsPwLayer">
        <div class="mypage-layer-content">
          <SnsPwLayer @close="handleCloseSnsPwLayer" @success="handleSnsPwSuccess" />
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Confirm 다이얼로그 -->
  <ConfirmComponent
    ref="dialogConfrim"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>

<style scoped lang="scss">
@media (max-width: 960px) {
  .card-content {
    flex: 1 1 100%; /* 작은 화면에서는 한 줄에 하나씩 배치 */
  }
}

.card-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  user-select: none;

  .v-field__input {
    margin-bottom: 0px !important;
    padding: 0px;
    color: var(--Gray-600, #757575);
    font-family: 'Pretendard';
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    padding: 14px;
  }

  .v-field__append-inner {
    height: 48px !important;
  }
}
</style>

<style lang="scss">
.mypage-info-container .button-section {
  padding: 0px 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .withdraw-button-wrap {
    margin: 8px 0px 20px;
    width: 100%;
    display: flex;
    justify-content: flex-end;

    button {
      color: var(--Gray-600, #757575);
      text-align: right;
      font-family: 'Pretendard';
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
      line-height: 16px;
      text-decoration-line: underline;
    }
  }

  .save-button {
    width: 260px;
  }
}

.mypage-info-container .inquiry-detail-header {
  display: none;
}
@media (max-width: 768px) {
  /* 컨테이너 스타일 */
  .mypage-info-container.card-container {
    padding: 0;
    padding-top: calc(52px + var(--safe-area-top, 0px) + 16px);
    padding-bottom: calc(var(--safe-area-bottom, 0px));
    width: 100%;
    max-width: 100%;
    min-height: 100dvh;
    justify-content: flex-start;
    gap: 24px;
    box-sizing: border-box;
    overflow: hidden;
    background: #f5f5f5 !important;
  }

  .mypage-info-container .button-section .withdraw-button-wrap {
    justify-content: center !important;

    button {
      text-align: center;
    }
  }

  /* 헤더 스타일 */
  .mypage-info-container .inquiry-detail-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    height: calc(52px + var(--safe-area-top, 0px));
    padding-top: var(--safe-area-top, 0px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-bottom: 1px solid #ebebeb;

    .inquiry-detail-header__back {
      position: absolute;
      left: 16px;
      top: calc((100% + var(--safe-area-top, 0px)) / 2);
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }

    .inquiry-detail-header__title {
      color: #333;
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
      text-align: center;
    }

    .inquiry-detail-header__right {
      position: absolute;
      right: 16px;
      top: calc((100% + var(--safe-area-top, 0px)) / 2);
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .inquiry-detail-header__edit {
      color: #eb4c10;
      font-family: Pretendard;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }

    .btn-mypage {
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;

      .icon-mypage {
        display: flex;
        background: url('@manseImages/icon-mypage.png') center center no-repeat;
        background-size: 32px auto;
        width: 32px;
        height: 32px;
        border-radius: 100%;
        font-size: 0;
      }
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;

      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;

        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }

        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }

      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;
        .txt1 {
          font-weight: 700;
        }
        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }

      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;

        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;
          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }
          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }

      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          background: #f5f5f5;
        }
        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);
          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }

  /* 카드 헤더 숨김 */
  .mypage-info-container :deep(.card-header) {
    display: none;
  }

  /* 버튼 섹션 스타일 */
  .mypage-info-container :deep(.button-section) {
    padding: 20px 16px;
    background: #fff;

    .withdraw-button-wrap {
      justify-content: center !important;

      button {
        text-align: center;
      }
    }
  }
}
.mypage-layer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);

  .mypage-layer-content {
    width: 100%;
    max-width: 520px;
    padding: 16px;
    box-sizing: border-box;
  }
}
</style>
